import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../institute-automation.css';
import PreviewSend from './PreviewSend';
import ModuleHeader from './ModuleHeader';
import { InstituteAutomationService, InstituteRow } from '../instituteAutomationService';

/** Standalone institute picker (live search, debounced) for the Preview & Send page. */
const InstitutePicker: React.FC<{ selected: InstituteRow | null; onSelect: (c: InstituteRow) => void }> = ({ selected, onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<InstituteRow[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const rows = await InstituteAutomationService.searchInstitutes(query.trim(), 20);
                if (!cancelled) setResults(rows);
            } catch (_) {
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 350 : 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query]);

    return (
        <div className="position-relative" ref={wrapRef} style={{ minWidth: 280 }}>
            <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0 text-secondary"><i className="bi bi-search" /></span>
                <input
                    type="text" className="form-control form-control-sm font-13 py-2 border-start-0"
                    placeholder={selected ? selected.name : 'Search a client to preview…'} autoComplete="off"
                    value={query} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    style={{ boxShadow: 'none' }}
                />
            </div>
            {open && (
                <ul className="dropdown-menu show w-100 shadow border border-light-subtle py-1 font-13" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}>
                    {loading ? (
                        <li className="dropdown-item py-2 text-muted font-13"><span className="spinner-border spinner-border-sm me-2" />Searching…</li>
                    ) : results.length === 0 ? (
                        <li className="dropdown-item py-2 text-muted font-13">No clients found</li>
                    ) : results.map((c) => (
                        <li key={c.id}>
                            <a className={`dropdown-item py-2 font-13 ${c.id === selected?.id ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); onSelect(c); setOpen(false); setQuery(''); }}>
                                {c.name} <span className="text-secondary font-10">· {c.id}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/** Standalone Preview & Send page (own route). Institute is deep-linkable via ?institute_id=. */
const PreviewSendPage: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const idFromUrl = params.get('institute_id');
    const nameFromUrl = params.get('name');

    const [institute, setInstitute] = useState<InstituteRow | null>(
        idFromUrl ? { id: idFromUrl, name: nameFromUrl || `Institute ${idFromUrl}` } : null
    );

    const [toastMsg, setToastMsg] = useState<string | null>(null);
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toast = (msg: string) => {
        setToastMsg('✔ ' + msg);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToastMsg(null), 3000);
    };
    useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

    const onSelect = (inst: InstituteRow) => {
        setInstitute(inst);
        setParams({ institute_id: inst.id, name: inst.name }); // keep the URL shareable
    };

    return (
        <div className="iar main p-3 p-md-4 bg-light flex-1">
            <div className="max-1250 mx-auto">
                <ModuleHeader active="preview" />

                {/* Institute selector for the preview */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                    <h5 className="mb-0 font-15 font-600 text-dark-blue">👁️ Preview &amp; Send</h5>
                    <InstitutePicker selected={institute} onSelect={onSelect} />
                </div>

                <PreviewSend toast={toast} onSchedule={() => toast('Use the module dashboard to set up the recurring schedule')} institute={institute} />
            </div>

            {/* TOAST */}
            <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1080 }}>
                <div className={`toast align-items-center text-white bg-dark border-0${toastMsg ? ' show' : ''}`} role="alert" aria-live="assertive" aria-atomic="true">
                    <div className="d-flex">
                        <div className="toast-body font-12 font-600 py-2">{toastMsg}</div>
                        <button type="button" className="btn-close btn-close-white me-2 m-auto" aria-label="Close" onClick={() => setToastMsg(null)} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviewSendPage;
